/**
 * ConflictDialog — modal shown when a markdown save returns 409 (Task 13).
 *
 * The dialog renders a side-by-side line-level diff of the user's draft
 * ("Yours") and the on-disk version ("Theirs"). The user can:
 *
 *   - **Overwrite**          — re-save with the disk version's
 *                              mtime/hash as the new expected values.
 *                              The lock is explicitly broken; the user
 *                              has seen the diff and decided their copy
 *                              wins.
 *   - **Reload and re-edit** — replace the editor buffer with the disk
 *                              version and rebase the baseline. No save
 *                              is issued; the user can keep editing.
 *   - **Cancel**             — close the dialog. The editor keeps the
 *                              dirty draft so the user can copy parts of
 *                              it before retrying.
 *
 * Acceptance (Task 13): "No silent overwrites possible" — the dialog
 * has no auto-dismiss path that issues a write. Every write goes
 * through the explicit "Overwrite" action.
 *
 * The dialog is rendered into the document body (best-effort portal —
 * we use a top-level fixed-position container rather than React's
 * `createPortal` to keep this file dependency-free; the SSR render path
 * remains safe because the component is gated on `state.phase === 'open'`).
 */

import { useEffect, useMemo, useRef } from 'react';

import type {
  ConflictState,
  UseConflictResolveResult,
} from '../hooks/useConflictResolve.js';
import {
  diffLines,
  summariseDiff,
  type DiffRow,
  type DiffResult,
} from '../lib/diff.js';

import type { MarkdownWriteConflict } from './MarkdownEditor.js';

/* ── Pure helpers (exposed for the smoke test) ────────────────────────── */

export interface ConflictHeader {
  readonly title: string;
  readonly summary: string;
  readonly mtimeOursLabel: string;
  readonly mtimeTheirsLabel: string;
}

/**
 * Pure: compute the header copy for the dialog. Exported so the smoke
 * test can assert on the user-facing strings without rendering React.
 */
export function buildConflictHeader(
  conflict: MarkdownWriteConflict,
  diff: DiffResult,
): ConflictHeader {
  const fmt = (ms: number): string => {
    if (!Number.isFinite(ms) || ms <= 0) return 'unknown';
    try {
      return new Date(ms).toISOString();
    } catch {
      return 'unknown';
    }
  };
  return {
    title: `Conflict: ${conflict.path}`,
    summary: `${summariseDiff(diff.stats)} — the file changed on disk while you were editing.`,
    mtimeOursLabel: `Your baseline · ${fmt(conflict.expectedMtime)}`,
    mtimeTheirsLabel: `On disk now · ${fmt(conflict.actualMtime)}`,
  };
}

/** CSS class hint for a single row, exported for test assertions. */
export function rowClassName(row: DiffRow): string {
  const base = 'ow-conflict-row';
  switch (row.kind) {
    case 'equal':
      return `${base} ow-conflict-row-equal`;
    case 'change':
      return `${base} ow-conflict-row-change`;
    case 'add':
      return `${base} ow-conflict-row-add`;
    case 'remove':
      return `${base} ow-conflict-row-remove`;
    case 'truncated':
      return `${base} ow-conflict-row-truncated`;
  }
}

/* ── Diff table sub-component ─────────────────────────────────────────── */

interface DiffTableProps {
  readonly diff: DiffResult;
}

function DiffTable(props: DiffTableProps): JSX.Element {
  const { rows } = props.diff;
  return (
    <table
      className="ow-conflict-diff"
      data-testid="conflict-dialog-diff"
      role="grid"
      aria-label="Side-by-side conflict diff"
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontFamily: 'var(--font-monospace, ui-monospace, Menlo, monospace)',
        fontSize: 12,
        lineHeight: '1.45em',
      }}
    >
      <thead>
        <tr>
          <th
            scope="col"
            colSpan={2}
            style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--background-modifier-border, #333)' }}
          >
            Yours (draft)
          </th>
          <th
            scope="col"
            colSpan={2}
            style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--background-modifier-border, #333)' }}
          >
            Theirs (on disk)
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr
            key={idx}
            className={rowClassName(row)}
            data-testid={`conflict-dialog-row-${idx}`}
            data-kind={row.kind}
            style={{
              background:
                row.kind === 'equal'
                  ? 'transparent'
                  : row.kind === 'change'
                  ? 'var(--color-yellow-rgb, rgba(255, 220, 100, 0.10))'
                  : row.kind === 'add'
                  ? 'rgba(80, 200, 120, 0.12)'
                  : row.kind === 'remove'
                  ? 'rgba(233, 49, 71, 0.12)'
                  : 'rgba(127, 109, 242, 0.10)',
            }}
          >
            <td
              style={{
                width: 40,
                textAlign: 'right',
                padding: '0 6px',
                color: 'var(--text-faint, #6b6b6b)',
                userSelect: 'none',
                verticalAlign: 'top',
              }}
            >
              {row.ourLine === null ? '' : row.ourLine + 1}
            </td>
            <td
              style={{
                padding: '0 6px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                verticalAlign: 'top',
                width: '45%',
              }}
            >
              {row.ours ?? ''}
            </td>
            <td
              style={{
                width: 40,
                textAlign: 'right',
                padding: '0 6px',
                color: 'var(--text-faint, #6b6b6b)',
                userSelect: 'none',
                verticalAlign: 'top',
                borderLeft: '1px solid var(--background-modifier-border, #333)',
              }}
            >
              {row.theirLine === null ? '' : row.theirLine + 1}
            </td>
            <td
              style={{
                padding: '0 6px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                verticalAlign: 'top',
                width: '45%',
              }}
            >
              {row.theirs ?? ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── Main dialog component ────────────────────────────────────────────── */

export interface ConflictDialogProps {
  readonly state: ConflictState;
  readonly onOverwrite: UseConflictResolveResult['overwrite'];
  readonly onReload: UseConflictResolveResult['reload'];
  readonly onCancel: UseConflictResolveResult['dismiss'];
}

export function ConflictDialog(props: ConflictDialogProps): JSX.Element | null {
  const { state } = props;
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const diff: DiffResult | null = useMemo(() => {
    if (state.conflict === null) return null;
    return diffLines(state.conflict.diff.ours, state.conflict.diff.theirs);
  }, [state.conflict]);

  const header: ConflictHeader | null = useMemo(() => {
    if (state.conflict === null || diff === null) return null;
    return buildConflictHeader(state.conflict, diff);
  }, [state.conflict, diff]);

  // Focus the dialog when it opens so Escape works immediately.
  useEffect(() => {
    if (state.phase !== 'idle' && dialogRef.current !== null) {
      dialogRef.current.focus();
    }
  }, [state.phase]);

  // Keyboard: Escape cancels.
  useEffect(() => {
    if (state.phase === 'idle') return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && state.phase !== 'resolving') {
        e.preventDefault();
        props.onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state.phase, props]);

  if (state.phase === 'idle' || state.conflict === null || diff === null || header === null) {
    return null;
  }

  const resolving = state.phase === 'resolving';

  return (
    <div
      className="ow-conflict-backdrop"
      data-testid="conflict-dialog-backdrop"
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        // Clicking the backdrop cancels (Obsidian-style).
        if (e.target === e.currentTarget && !resolving) props.onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="ow-conflict-dialog"
        data-testid="conflict-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ow-conflict-title"
        aria-describedby="ow-conflict-summary"
        tabIndex={-1}
        style={{
          background: 'var(--background-primary, #1e1e1e)',
          color: 'var(--text-normal, #dcddde)',
          border: '1px solid var(--background-modifier-border, #333)',
          borderRadius: 6,
          width: 'min(1100px, 92vw)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      >
        <header
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--background-modifier-border, #333)',
          }}
        >
          <h2
            id="ow-conflict-title"
            data-testid="conflict-dialog-title"
            style={{ margin: 0, fontSize: 14, fontWeight: 600 }}
          >
            {header.title}
          </h2>
          <p
            id="ow-conflict-summary"
            data-testid="conflict-dialog-summary"
            style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted, #a1a1aa)' }}
          >
            {header.summary}
          </p>
          <div
            style={{
              display: 'flex',
              gap: 16,
              marginTop: 8,
              fontSize: 11,
              color: 'var(--text-faint, #6b6b6b)',
            }}
          >
            <span data-testid="conflict-dialog-mtime-ours">{header.mtimeOursLabel}</span>
            <span data-testid="conflict-dialog-mtime-theirs">{header.mtimeTheirsLabel}</span>
          </div>
        </header>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 0' }}>
          <DiffTable diff={diff} />
        </div>

        {state.lastError !== null ? (
          <div
            data-testid="conflict-dialog-error"
            style={{
              padding: '8px 16px',
              color: 'var(--text-error, #e93147)',
              borderTop: '1px solid var(--background-modifier-border, #333)',
              fontSize: 12,
            }}
          >
            {state.lastError.code}: {state.lastError.message}
          </div>
        ) : null}

        <footer
          style={{
            padding: '10px 16px',
            borderTop: '1px solid var(--background-modifier-border, #333)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            data-testid="conflict-dialog-cancel"
            disabled={resolving}
            onClick={() => props.onCancel()}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="conflict-dialog-reload"
            disabled={resolving}
            onClick={() => props.onReload()}
            title="Replace the editor with the on-disk version. Your draft is discarded."
          >
            Reload and re-edit
          </button>
          <button
            type="button"
            data-testid="conflict-dialog-overwrite"
            disabled={resolving}
            onClick={() => {
              void props.onOverwrite();
            }}
            title="Save your draft, replacing the on-disk version."
            style={{ fontWeight: 600 }}
          >
            {resolving ? 'Saving…' : 'Overwrite'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default ConflictDialog;
